import { CopyOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { Button, Card, Col, Divider, Layout, Row, Spin, Tooltip, Typography, message } from 'antd';
import { useEffect, useState } from 'react';

interface ContactMember {
  name: string;
  role: string | null;
  phone: string;
  email: string;
}

interface ContactTeam {
  name: string;
  email: string;
  members: ContactMember[];
}

interface ContactsData {
  management: {
    title: string;
    members: ContactMember[];
  };
  teams: ContactTeam[];
}

function ContactInfo({ phone, email }: { phone: string; email: string }) {
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <PhoneOutlined style={{ fontSize: 14 }} />
        <Typography.Link href={`tel:${phone}`}>{phone}</Typography.Link>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <MailOutlined style={{ fontSize: 14 }} />
        <Typography.Link href={`mailto:${email}`}>{email}</Typography.Link>
      </div>
    </div>
  );
}

export function ContactsPage() {
  const [data, setData] = useState<ContactsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void fetch('/contacts.json')
      .then((res) => res.json())
      .then((json: ContactsData) => setData(json))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size='large' />
      </div>
    );
  }

  if (!data) return null;

  return (
    <Layout>
      <Layout.Content>
        <Typography.Title level={2}>Kontakte</Typography.Title>
        <Typography.Paragraph>
          Lieber Partner, wenden Sie sich bei Fragen und für weiterführende Informationen an das
          Team der mail alliance – wir helfen Ihnen gerne weiter!
        </Typography.Paragraph>

        <Card
          title={data.management.title}
          style={{ marginBottom: 24 }}
          styles={{ body: { paddingTop: 12 } }}
        >
          {data.management.members.map((member, idx) => (
            <div key={member.email}>
              <Typography.Text strong>
                {member.name}
                {member.role && <Typography.Text type='secondary'>, {member.role}</Typography.Text>}
              </Typography.Text>
              <ContactInfo phone={member.phone} email={member.email} />
              {idx < data.management.members.length - 1 && <Divider style={{ margin: '12px 0' }} />}
            </div>
          ))}
        </Card>

        <Row gutter={[24, 24]}>
          {data.teams.map((team) => (
            <Col xs={24} lg={24} key={team.name}>
              <Card
                title={team.name}
                style={{ height: '100%' }}
                styles={{ body: { paddingTop: 12 } }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <MailOutlined style={{ fontSize: 14 }} />
                  <Typography.Link href={`mailto:${team.email}`}>{team.email}</Typography.Link>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <Row gutter={[24, 24]}>
                  {team.members.map((member) => (
                    <Col xs={24} sm={12} lg={6} key={member.email}>
                      <Typography.Text strong>
                        {member.name}
                        {member.role && (
                          <Typography.Text type='secondary'>, {member.role}</Typography.Text>
                        )}
                      </Typography.Text>
                      <ContactInfo phone={member.phone} email={member.email} />
                    </Col>
                  ))}
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
      </Layout.Content>
    </Layout>
  );
}
